import { forwardRef, Injectable } from "@angular/core";
import { isNil } from "lodash-es";

import { LoggingFactory } from "../logging/logging.factory";
import { Log } from "../logging/logging.model";
import { DocumentRef } from "../platform/document";

import { Cookie, CookieOptions } from "./cookie.model";

@Injectable({
	providedIn: "root",
	// eslint-disable-next-line @angular-eslint/no-forward-ref
	useClass: forwardRef(() => ClientCookieService),
})
export abstract class CookieService {
	private logger: Log;

	constructor(logging: LoggingFactory) {
		this.logger = logging.get("CookieService");
	}

	abstract get(name: string): string | undefined;

	abstract set(cookie: Cookie): void;

	abstract delete(name: string, config?: CookieOptions): void;

	abstract getAll(predicate?: (key: string) => boolean): Record<string, string> | undefined;

	setJson(cookie: Cookie) {
		this.set({ ...cookie, value: JSON.stringify(cookie.value) });
	}

	getJson<T>(name: string): T | undefined {
		const rawCookie = this.get(name);
		if (rawCookie) {
			try {
				return JSON.parse(rawCookie);
			} catch (error) {
				this.logger.error("getJson", "Could not parse cookie", { rawCookie, error });
			}
		}
		return undefined;
	}

	protected getCookieRegExp(name: string): RegExp {
		const escapedName = name.replace(/([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/gi, "\\$1");
		return new RegExp(`(?:^${escapedName}|;\\s*${escapedName})=(.*?)(?:;|$)`, "gi");
	}
}

@Injectable({
	providedIn: "root",
})
export class ClientCookieService extends CookieService {
	constructor(
		private documentRef: DocumentRef,
		logging: LoggingFactory
	) {
		super(logging);
	}

	get(name: string): string | undefined {
		const cookie = this.getCookieRegExp(name).exec(this.documentRef.native.cookie);

		if (isNil(cookie)) {
			return undefined;
		}
		return decodeURIComponent(cookie[1]);
	}

	getAll(predicate?: (key: string) => boolean): Record<string, string> | undefined {
		if (!this.documentRef.native.cookie) {
			return undefined;
		}

		const pairs = this.documentRef.native.cookie.split(";");
		const cookies: Record<string, string> = {};
		for (const pair of pairs) {
			const [key, value] = pair.split("=");

			if (!predicate || predicate(key)) {
				cookies[key.trim()] = decodeURIComponent(value);
			}
		}

		return cookies;
	}

	set(cookie: Cookie) {
		let newCookie = `${cookie.name}=${encodeURIComponent(cookie.value)};`;

		if (cookie.expiryDate) {
			newCookie += `expires=${cookie.expiryDate.toUTCString()};`;
		}

		if (cookie.secure) {
			newCookie += "secure;";
		}

		if (cookie.sameSite) {
			newCookie += `sameSite=${cookie.sameSite};`;
		}

		if (!cookie.isSubdomainSpecific) {
			const domain =
				cookie.name === "CONSENT"
					? cookie.domain ||
						this.documentRef.native.location!.hostname.replace(/^(?:.*?\.)?([^.]+\.[^.]+)$/, "$1")
					: cookie.domain || this.documentRef.native.location!.hostname.replace(/^www(.*?)\./, "");
			if (domain) {
				newCookie += `domain=${domain};`;
			}
		}

		newCookie += cookie.path ? `path=${cookie.path};` : "path=/;";

		this.documentRef.native.cookie = newCookie;
	}

	delete(name: string, config?: CookieOptions) {
		const value = this.get(name);
		if (!value) {
			return;
		}
		const expiryDate = new Date();
		expiryDate.setTime(expiryDate.getTime() - 1000 * 60 * 60 * 24);

		const cookieToModify = {
			name,
			value,
			expiryDate,
		} as Cookie;

		if (config && config.domain) {
			cookieToModify.domain = config.domain;
		}

		this.set(cookieToModify);
	}
}
